import { IconLoader } from '@tabler/icons-react'
import { cn } from '@/lib/utils'
import { FormControl } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTranslation } from 'react-i18next'

interface SelectDropdownProps {
  onValueChange?: (value: string) => void
  defaultValue: string | undefined
  placeholder?: string
  isPending?: boolean
  items: { label: string; value: string }[] | undefined
  disabled?: boolean
  className?: string
  isControlled?: boolean
  triggerId?: string
  /** Force the error styling (red border) on the trigger. */
  isInvalid?: boolean
}

export function SelectDropdown({
  defaultValue,
  onValueChange,
  isPending,
  items,
  placeholder,
  disabled,
  className = '',
  isControlled = false,
  triggerId,
  isInvalid = false,
}: SelectDropdownProps) {
  const i18n = useTranslation()
  // Radix Select forbids an empty-string value and breaks its controlled
  // round-trip when given one (onValueChange fires + the label shows, but the
  // value never settles back through the controlled binding). Coerce '' -> undefined
  // so an empty selection is "no value" instead of the reserved empty string.
  const defaultState = isControlled
    ? { value: defaultValue || undefined, onValueChange }
    : { defaultValue, onValueChange }
  return (
    <Select {...defaultState}>
      <FormControl>
        <SelectTrigger
          disabled={disabled}
          className={cn(className)}
          data-testid={triggerId}
          state={isInvalid ? 'error' : undefined}
        >
          <SelectValue placeholder={placeholder ?? 'Select'} />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        {isPending ? (
          <SelectItem disabled value='loading' className='h-14'>
            <div className='flex items- justify-center gap-2'>
              <IconLoader className='h-5 w-5 animate-spin' />
              {'  '}
              {i18n.t('loading')}
            </div>
          </SelectItem>
        ) : (
          items?.map(({ label, value }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  )
}
